import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle, Circle, AlertCircle, Upload } from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';

const STATUS_ICONS: Record<string, React.ReactNode> = {
  empty: <Circle className="h-4 w-4 text-gray-300" />,
  partial: <AlertCircle className="h-4 w-4 text-yellow-500" />,
  extracted: <Circle className="h-4 w-4 text-blue-500" />,
  verified: <CheckCircle className="h-4 w-4 text-green-500" />,
  analyzed: <CheckCircle className="h-4 w-4 text-green-600" />,
};

const STATUS_LABELS: Record<string, string> = {
  empty: 'No data',
  partial: 'Incomplete',
  extracted: 'Needs review',
  verified: 'Verified',
  analyzed: 'Analyzed',
};

export function ProfileCompleteness() {
  const { data } = useQuery({
    queryKey: ['profileCompleteness'],
    queryFn: () => api.getProfileCompleteness(),
  });

  if (!data) return null;

  const sections = [
    { key: 'household', label: 'Household', data: data.sections.household },
    { key: 'income', label: 'Income', data: data.sections.income },
    { key: 'tax', label: 'Tax', data: data.sections.tax },
    { key: 'insurance', label: 'Insurance', data: data.sections.insurance },
    { key: 'estate', label: 'Estate', data: data.sections.estate },
  ];

  return (
    <div className="bg-white rounded-lg border p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Your Financial Profile</h3>
        <span className="text-sm font-medium text-gray-900">{data.overall}%</span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
        <div
          className={clsx('h-2 rounded-full transition-all', data.overall >= 85 ? 'bg-green-500' : data.overall >= 50 ? 'bg-blue-500' : 'bg-yellow-500')}
          style={{ width: `${data.overall}%` }}
        />
      </div>

      {/* Section breakdown */}
      <div className="space-y-2 mb-4">
        {sections.map((s) => (
          <div key={s.key} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              {STATUS_ICONS[s.data.status]}
              <span className="text-gray-700">{s.label}</span>
            </div>
            <span className="text-gray-500">{STATUS_LABELS[s.data.status]}</span>
          </div>
        ))}
      </div>

      {/* Unverified facts */}
      {data.unverifiedFactCount > 0 && (
        <Link
          to="/documents"
          className="block text-sm text-orange-600 hover:text-orange-700 mb-3"
        >
          {data.unverifiedFactCount} fact{data.unverifiedFactCount > 1 ? 's' : ''} need review &rarr;
        </Link>
      )}

      {/* Missing document suggestions */}
      {data.missingDocuments.length > 0 && (
        <div className="border-t pt-3 mt-3">
          {data.missingDocuments.slice(0, 2).map((doc: any) => (
            <Link
              key={doc.documentType}
              to="/documents"
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 py-1"
            >
              <Upload className="h-3.5 w-3.5" />
              <span>{doc.label}</span>
              {doc.unlocks && <span className="text-xs text-gray-400">— unlocks {doc.unlocks}</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
